// Casca da interface: faixa arco-íris, menu lateral de 4 pais recolhíveis com
// "expandir/recolher tudo", cabeçalho com o usuário e o botão de backup, e a
// área de conteúdo (cada tela é desenhada por `renderScreen`).

import { navVisivel, screenLabel, telasEngrenagemVisiveis, type PermiteModulo } from "./nav.ts";
import { clear, el } from "./ui.ts";
import { icon, iconeOpcional } from "./icons.ts";

const FOCAVEIS_MENU = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

export interface ShellCallbacks {
  onNavigate(screenId: string): void;
  onBackup(): void;
  /** Só na versão web: mostra um botão "Sair" e o nome no topo. */
  onLogout?(): void;
  /** Nome do usuário logado (só na versão web). */
  usuarioNome?: string;
  /** Perfil do usuário logado tem ao menos leitura neste Módulo? (issue #71) */
  permiteModulo?: PermiteModulo;
  /**
   * Desenha a tela na área de conteúdo. Retorna `false` se não a conhece, ou
   * uma função de limpeza a chamar quando o usuário sair da tela.
   */
  renderScreen(screenId: string, container: HTMLElement): boolean | (() => void);
}

export interface Shell {
  showScreen(screenId: string): void;
}

/**
 * Quais grupos do menu ficam abertos. Padrão: **todos abertos** — quem entra
 * enxerga o sistema inteiro. A escolha de recolher é lembrada por navegador,
 * como as larguras de coluna das tabelas.
 */
const CHAVE_MENU = "menu:grupos-fechados";

function gruposFechados(): Set<string> {
  try {
    const cru = localStorage.getItem(CHAVE_MENU);
    return new Set(cru ? (JSON.parse(cru) as string[]) : []);
  } catch {
    return new Set(); // storage bloqueado: cai no padrão (tudo aberto)
  }
}

function salvarGruposFechados(fechados: ReadonlySet<string>): void {
  try {
    localStorage.setItem(CHAVE_MENU, JSON.stringify([...fechados]));
  } catch {
    /* sem storage: a escolha vale só nesta sessão */
  }
}

export function mountShell(root: HTMLElement, cb: ShellCallbacks): Shell {
  clear(root);
  root.append(el("div", { class: "rainbow" }));

  const layout = el("div", { class: "layout" });
  root.append(layout);

  const fecharMenuMobile = (): void => {
    const estavaAberto = layout.classList.contains("menu-aberto");
    layout.classList.remove("menu-aberto");
    // achado de acessibilidade: antes o foco ficava onde estivesse depois
    // de fechar a gaveta (link clicado, botão ×, fundo escurecido) — agora
    // sempre volta pro ☰ que abriu ela, que continua visível na tela.
    if (estavaAberto) menuBtn.focus();
  };

  // fundo escurecido do menu no mobile — clicar fora fecha
  const menuOverlay = el("div", { class: "menu-overlay", "aria-hidden": "true" });
  menuOverlay.addEventListener("click", fecharMenuMobile);

  // --- menu lateral ---
  const aside = el("aside", { class: "sidebar" });
  const fecharMenu = el(
    "button",
    { type: "button", class: "menu-fechar", "aria-label": "Fechar Menu" },
    icon("close", 18),
  );
  fecharMenu.addEventListener("click", fecharMenuMobile);
  aside.append(
    el(
      "div",
      { class: "brand" },
      el("img", { src: "assets/logo-dark.svg", alt: "Arco-Íris Lavanderia" }),
      fecharMenu,
    ),
  );

  const groupEls: HTMLDetailsElement[] = [];
  const fechados = gruposFechados();
  const btnExpand = el("button", { type: "button" }, "Expandir Tudo");
  const btnCollapse = el("button", { type: "button" }, "Recolher Tudo");
  btnExpand.addEventListener("click", () => {
    for (const g of groupEls) g.open = true;
  });
  btnCollapse.addEventListener("click", () => {
    for (const g of groupEls) g.open = false;
  });
  aside.append(el("div", { class: "menu-toolbar" }, btnExpand, btnCollapse));

  const nav = el("nav", { class: "menu" });
  for (const group of navVisivel(cb.permiteModulo)) {
    if (group.children.length === 0) {
      const b = el(
        "button",
        { type: "button", class: "menu-parent", "data-screen": group.id },
        ...iconeOpcional(group.icone),
        el("span", {}, group.label),
      );
      b.addEventListener("click", () => {
        cb.onNavigate(group.id);
        fecharMenuMobile();
      });
      nav.append(b);
      continue;
    }
    const det = el("details", { class: "menu-group" }) as HTMLDetailsElement;
    // nasce aberto; só fica fechado se a pessoa recolheu antes neste navegador
    det.open = !fechados.has(group.id);
    det.addEventListener("toggle", () => {
      if (det.open) fechados.delete(group.id);
      else fechados.add(group.id);
      salvarGruposFechados(fechados);
    });
    det.append(
      el(
        "summary",
        {},
        ...iconeOpcional(group.icone),
        el("span", {}, group.label),
      ),
    );
    const ul = el("ul");
    for (const child of group.children) {
      const link = el(
        "button",
        { type: "button", "data-screen": child.id },
        ...iconeOpcional(child.icone),
        el("span", {}, child.label),
      );
      link.addEventListener("click", () => {
        cb.onNavigate(child.id);
        fecharMenuMobile();
      });
      ul.append(el("li", {}, link));
    }
    det.append(ul);
    groupEls.push(det);
    nav.append(det);
  }
  aside.append(nav);
  layout.append(menuOverlay, aside);

  // --- área principal ---
  const menuBtn = el(
    "button",
    { type: "button", class: "menu-toggle", "aria-label": "Menu" },
    "☰",
  );
  menuBtn.addEventListener("click", () => {
    const abrindo = layout.classList.toggle("menu-aberto");
    if (abrindo) fecharMenu.focus();
  });
  // Tab preso dentro da gaveta enquanto ela estiver aberta (achado de
  // acessibilidade) — o próprio botão ☰ só fica visível/clicável no
  // celular (CSS), então isso nunca interfere no desktop.
  document.addEventListener("keydown", (ev) => {
    if (ev.key !== "Tab" || !layout.classList.contains("menu-aberto")) return;
    const focaveis = [...aside.querySelectorAll<HTMLElement>(FOCAVEIS_MENU)];
    if (focaveis.length === 0) return;
    const primeiro = focaveis[0]!;
    const ultimo = focaveis[focaveis.length - 1]!;
    if (ev.shiftKey && document.activeElement === primeiro) {
      ev.preventDefault();
      ultimo.focus();
    } else if (!ev.shiftKey && document.activeElement === ultimo) {
      ev.preventDefault();
      primeiro.focus();
    }
  });
  const title = el("h1", { class: "screen-title" }, "Dashboard");
  const acoesTopo: Node[] = [];

  if (cb.usuarioNome) {
    acoesTopo.push(el("span", { class: "topo-usuario" }, cb.usuarioNome));
  }

  // menu da engrenagem (⚙): telas de administração + fazer backup
  {
    const engrenagemItens = telasEngrenagemVisiveis(cb.permiteModulo);
    const drop = el("details", { class: "config-drop" }) as HTMLDetailsElement;
    drop.append(
      el(
        "summary",
        { class: "btn-backup config-btn", "aria-label": "Configurações", title: "Configurações" },
        icon("parametros", 18, { peso: 1.8 }),
      ),
    );
    const lista = el("div", { class: "config-menu" });
    for (const t of engrenagemItens) {
      const item = el(
        "button",
        { type: "button" },
        ...iconeOpcional(t.icone),
        el("span", {}, t.label),
      );
      item.addEventListener("click", () => {
        drop.open = false;
        cb.onNavigate(t.id);
        fecharMenuMobile();
      });
      lista.append(item);
    }
    if (engrenagemItens.length > 0) lista.append(el("hr", { class: "config-sep" }));
    const backup = el(
      "button",
      { type: "button" },
      icon("download", 16),
      el("span", {}, "Fazer Backup"),
    );
    backup.addEventListener("click", () => {
      drop.open = false;
      cb.onBackup();
    });
    lista.append(backup);
    // Versão do build rodando (issue #63) — informação de apoio, não uma
    // ação: nunca clicável, sempre a última linha do menu.
    lista.append(
      el("div", { class: "config-versao muted" }, `v${__VERSION__} · ${__BUILD__}`),
    );
    drop.append(lista);
    // fecha ao clicar fora — listener só existe enquanto está aberto
    const fora = (e: Event): void => {
      if (!drop.contains(e.target as Node)) drop.open = false;
    };
    drop.addEventListener("toggle", () => {
      if (drop.open) document.addEventListener("pointerdown", fora);
      else document.removeEventListener("pointerdown", fora);
    });
    acoesTopo.push(drop);
  }

  if (cb.onLogout) {
    const sair = el("button", { type: "button", class: "btn-backup" }, "Sair");
    sair.addEventListener("click", () => cb.onLogout?.());
    acoesTopo.push(sair);
  }
  const content = el("section", { class: "content" });
  layout.append(
    el(
      "main",
      { class: "main" },
      el(
        "header",
        { class: "topbar" },
        el("div", { class: "topbar-left" }, menuBtn, title),
        el("div", { class: "topbar-right" }, ...acoesTopo),
      ),
      content,
    ),
  );

  let limparTela: (() => void) | undefined;

  return {
    showScreen(screenId) {
      limparTela?.();
      limparTela = undefined;
      title.textContent = screenLabel(screenId);
      clear(content);
      const resultado = cb.renderScreen(screenId, content);
      if (resultado === false) {
        content.append(
          el(
            "div",
            { class: "stub" },
            el("p", {}, `A tela “${screenLabel(screenId)}” será construída num ticket próprio.`),
          ),
        );
      } else if (typeof resultado === "function") {
        limparTela = resultado;
      }
      for (const b of nav.querySelectorAll("button[data-screen]")) {
        b.classList.toggle(
          "active",
          b.getAttribute("data-screen") === screenId,
        );
      }
    },
  };
}
